import { useState } from 'react';
import { X, CreditCard, ChevronDown, ChevronUp } from 'lucide-react';
import type { OwnedCard, XmCardBrand } from '@/types/database';
import { XM_CARD_BRANDS, CARD_POOL_MAP, CARD_POOL_LIMITS } from '@/types/database';
import { getCardBrandColor } from '../customers/cardBrandColors';

interface CardInfo {
  card_number: string;
  expiry_date: string;
  cvv: string;
}

interface Props {
  onClose: () => void;
  onSave: (name: string, cards: OwnedCard[], email?: string) => void;
}

export default function AddGlobalCustomerModal({ onClose, onSave }: Props) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [selectedCards, setSelectedCards] = useState<XmCardBrand[]>([]);
  const [cardInfoMap, setCardInfoMap] = useState<Record<string, CardInfo>>({});
  const [expandedCard, setExpandedCard] = useState<string | null>(null);

  function toggleCard(brand: XmCardBrand) {
    setSelectedCards(prev => {
      if (prev.includes(brand)) {
        setCardInfoMap(m => {
          const copy = { ...m };
          delete copy[brand];
          return copy;
        });
        if (expandedCard === brand) setExpandedCard(null);
        return prev.filter(b => b !== brand);
      }
      setExpandedCard(brand);
      return [...prev, brand];
    });
  }

  function updateCardInfo(brand: string, field: keyof CardInfo, value: string) {
    setCardInfoMap(prev => ({
      ...prev,
      [brand]: { ...prev[brand], [field]: value },
    }));
  }

  function formatCardNumber(value: string): string {
    const digits = value.replace(/\D/g, '').slice(0, 16);
    return digits.replace(/(.{4})/g, '$1 ').trim();
  }

  function formatExpiry(value: string): string {
    const digits = value.replace(/\D/g, '').slice(0, 4);
    if (digits.length > 2) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
    return digits;
  }

  function handleSubmit() {
    const trimmed = name.trim();
    if (!trimmed) { alert('Enter a customer name'); return; }
    if (selectedCards.length === 0) { alert('Select at least one card'); return; }

    const cards: OwnedCard[] = selectedCards.map(type => {
      const info = cardInfoMap[type];
      return {
        type,
        pool: CARD_POOL_MAP[type],
        limit_usd: CARD_POOL_LIMITS[CARD_POOL_MAP[type]],
        ...(info?.card_number && { card_number: info.card_number.replace(/\s/g, '') }),
        ...(info?.expiry_date && { expiry_date: info.expiry_date }),
        ...(info?.cvv && { cvv: info.cvv }),
      };
    });

    onSave(trimmed, cards, email.trim() || undefined);
  }

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-md flex items-center justify-center z-50 p-4">
      <div className="glass-modal rounded-2xl max-w-md w-full max-h-[85vh] flex flex-col">
        <div className="flex items-center justify-between p-5 border-b border-white/10">
          <h2 className="text-base font-bold text-white">Add Customer</h2>
          <button
            onClick={onClose}
            className="text-white hover:text-white p-1 glass-hover rounded-lg"
          >
            <X size={18} />
          </button>
        </div>
        <div className="flex-1 overflow-auto p-5 space-y-4">
          <div>
            <label className="block text-xs font-semibold text-white mb-1.5">Customer Name</label>
            <input
              type="text"
              placeholder="Enter name"
              value={name}
              onChange={e => setName(e.target.value)}
              autoFocus
              className="w-full px-4 py-2.5 bg-white/10 border border-white/10 rounded-xl text-sm focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 outline-none transition-all duration-200 placeholder:text-white/40"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-white mb-1.5">Email (optional)</label>
            <input
              type="email"
              placeholder="customer@email.com"
              value={email}
              onChange={e => setEmail(e.target.value)}
              className="w-full px-4 py-2.5 bg-white/10 border border-white/10 rounded-xl text-sm focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 outline-none transition-all duration-200 placeholder:text-white/40"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-white mb-2">Owned Cards</label>
            <div className="grid grid-cols-2 gap-2">
              {XM_CARD_BRANDS.map(brand => {
                const color = getCardBrandColor(brand);
                const isSelected = selectedCards.includes(brand);
                const pool = CARD_POOL_MAP[brand];
                const limit = CARD_POOL_LIMITS[pool];
                return (
                  <button
                    key={brand}
                    type="button"
                    onClick={() => toggleCard(brand)}
                    className={`flex items-center gap-2 px-3 py-2.5 rounded-xl border text-left text-xs font-medium transition-all duration-200 ${
                      isSelected
                        ? 'border-teal-500 bg-teal-500/10 text-teal-800 ring-2 ring-teal-500/20'
                        : 'border-white/10 bg-white/10 text-white glass-hover'
                    }`}
                  >
                    <div className={`w-2.5 h-2.5 rounded-full shrink-0 ${color.dot}`} />
                    <div className="min-w-0">
                      <p className="truncate">{brand}</p>
                      <p className="text-[10px] text-white">${limit.toLocaleString()}</p>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {selectedCards.length > 0 && (
            <div className="space-y-2">
              <p className="text-[10px] font-semibold text-white uppercase tracking-wider">Selected Cards</p>
              {selectedCards.map(brand => {
                const color = getCardBrandColor(brand);
                const pool = CARD_POOL_MAP[brand];
                const limit = CARD_POOL_LIMITS[pool];
                const isExpanded = expandedCard === brand;
                const info = cardInfoMap[brand] || { card_number: '', expiry_date: '', cvv: '' };
                return (
                  <div key={brand} className="glass-inner rounded-xl overflow-hidden">
                    <button
                      type="button"
                      onClick={() => setExpandedCard(isExpanded ? null : brand)}
                      className="w-full flex items-center justify-between px-3 py-2.5 text-xs"
                    >
                      <div className="flex items-center gap-2">
                        <div className={`w-2 h-2 rounded-full ${color.dot}`} />
                        <span className="text-white font-medium">{brand}</span>
                        <span className="text-white">${limit.toLocaleString()}</span>
                      </div>
                      <div className="flex items-center gap-1.5 text-white">
                        {(info.card_number || info.expiry_date || info.cvv) && (
                          <CreditCard size={11} className="text-teal-500" />
                        )}
                        {isExpanded ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
                      </div>
                    </button>
                    {isExpanded && (
                      <div className="px-3 pb-3 space-y-2 border-t border-white/10/60">
                        <p className="text-[10px] font-semibold text-white uppercase tracking-wider pt-2">Card Info (optional)</p>
                        <input
                          type="text"
                          placeholder="Card Number"
                          value={info.card_number ? formatCardNumber(info.card_number) : ''}
                          onChange={e => updateCardInfo(brand, 'card_number', e.target.value.replace(/\D/g, '').slice(0, 16))}
                          className="w-full px-3 py-2 bg-white/10 border border-white/10 rounded-lg text-xs focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 outline-none transition-all duration-200 placeholder:text-white/40 tracking-wider"
                          maxLength={19}
                        />
                        <div className="flex gap-2">
                          <input
                            type="text"
                            placeholder="MM/YY"
                            value={info.expiry_date ? formatExpiry(info.expiry_date.replace(/\//g, '')) : ''}
                            onChange={e => updateCardInfo(brand, 'expiry_date', formatExpiry(e.target.value))}
                            className="flex-1 px-3 py-2 bg-white/10 border border-white/10 rounded-lg text-xs focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 outline-none transition-all duration-200 placeholder:text-white/40 tracking-wider"
                            maxLength={5}
                          />
                          <input
                            type="text"
                            placeholder="CVV"
                            value={info.cvv || ''}
                            onChange={e => updateCardInfo(brand, 'cvv', e.target.value.replace(/\D/g, '').slice(0, 4))}
                            className="w-20 px-3 py-2 bg-white/10 border border-white/10 rounded-lg text-xs focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 outline-none transition-all duration-200 placeholder:text-white/40 tracking-wider"
                            maxLength={4}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
        <div className="flex gap-3 p-5 border-t border-white/10">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2.5 text-sm glass-button text-white rounded-xl font-medium"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            className="flex-1 px-4 py-2.5 text-sm glass-button-active text-white rounded-xl font-medium"
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
}
